import React, { createContext, useContext, useLayoutEffect, useRef } from 'react';
import { Outlet, useLocation, Navigate } from 'react-router-dom';
import EnteteSimple from './EnteteSimple';
import MenuOAD from './MenuOAD';
import { OAD_NAV, OAD_ROOT } from '../OAD/shared/navConfig';
import { api } from '../OAD/shared/api/api';
import { useApiData } from '../OAD/shared/hooks/useFetch';

/**
 * Déduit la position dans l'arbre à partir de l'URL.
 * /oad/2035-2050/eau-irrigation/besoin-en-eau -> niveau1, niveau2, niveau3
 * Le Tableau de bord n'a pas de niveau 1 : /oad/tableau-de-bord.
 */
function lirePosition(pathname) {
  const segments = pathname.replace(OAD_ROOT, '').split('/').filter(Boolean);
  const [premier, deuxieme, troisieme] = segments;
  if (!premier || !OAD_NAV[premier]) {
    return { niveau1: null, niveau2: null, niveau3: null };
  }
  return { niveau1: premier, niveau2: deuxieme ?? null, niveau3: troisieme ?? null };
}

const ContexteOAD = createContext(null);

/**
 * Donne accès à idExploit et à la position courante dans l'arbre de navigation.
 * Évite que chaque page refasse useLocation().state.
 */
export function useOAD() {
  const contexte = useContext(ContexteOAD);
  if (!contexte) {
    throw new Error('useOAD doit être utilisé à l\'intérieur de OADLayout');
  }
  return contexte;
}

/**
 * Coquille commune des pages OAD : entete_simple + Menu_OAD + contenu.
 *
 * La Side_bar_parametres n'est pas montée ici mais par chaque page, parce que
 * ses filtres diffèrent d'une page à l'autre ; seule sa partie navigation est
 * commune et vient de navConfig.js.
 *
 * idExploit continue de circuler en state du routeur (Q2). Il est lu une seule
 * fois ici et redistribué par contexte. Si le state est absent (rafraîchissement
 * de page), on renvoie vers la liste des simulations plutôt que de laisser la
 * page planter sur un idExploit indéfini.
 */
export default function OADLayout() {
  const { state, pathname } = useLocation();
  const { niveau1, niveau2, niveau3 } = lirePosition(pathname);
  const idExploit = state?.idExploit;

  // Région agricole de l'exploitation, affichée sous le titre de l'entête sur
  // toutes les pages de l'OAD. Même appel (mêmes paramètres) que le Tableau de
  // bord : le cache de useApiData évite un second appel réseau.
  const parametresExploitation = React.useMemo(
    () => ({ idExploitation: idExploit }),
    [idExploit]
  );
  const { data: exploitation } = useApiData(api.getExploitation, parametresExploitation, {
    skip: idExploit === undefined,
  });
  const regionAgricole =
    typeof exploitation === 'object' && exploitation !== null ? exploitation.libelleRA : undefined;

  // La sidebar est sticky sous les barres fixes. Leur hauteur varie (le menu a
  // une ou deux lignes selon la page), donc on la mesure au lieu de la coder en
  // dur, et on l'expose en variable CSS pour que le CSS s'en serve.
  const refBarres = useRef(null);
  const refPage = useRef(null);

  useLayoutEffect(() => {
    const barres = refBarres.current;
    const page = refPage.current;
    if (!barres || !page) return undefined;

    const mesurer = () =>
      page.style.setProperty('--oad-barres-hauteur', `${barres.offsetHeight}px`);

    mesurer();
    const observateur = new ResizeObserver(mesurer);
    observateur.observe(barres);
    return () => observateur.disconnect();
  }, [niveau1, niveau2]);

  if (idExploit === undefined) {
    return <Navigate to="/simulations" replace />;
  }

  const titre = niveau1 ? OAD_NAV[niveau1]?.label : 'Tableau de bord';

  return (
    <ContexteOAD.Provider value={{ idExploit, niveau1, niveau2, niveau3 }}>
      <div className="oad-page" ref={refPage}>
        <div className="oad-barres-fixes" ref={refBarres}>
          <EnteteSimple titre={titre} sousTitre={regionAgricole} />
          <MenuOAD niveau1={niveau1} niveau2={niveau2} idExploit={idExploit} />
        </div>
        <div className="oad-corps">
          <Outlet />
        </div>
      </div>
    </ContexteOAD.Provider>
  );
}
